import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Database, AlertTriangle, DollarSign, MapPin, ArrowUpRight } from 'lucide-react';
import { STATEWIDE_TOTAL, DISTRICTS, STATEWIDE_GRADES, DWS_DATA, COST_CATEGORIES, JURISDICTIONS, calcCostRange } from '../data/vdot.js';
import { fmt, fmtCurrency, fmtPct, Pill, StatCard, SectionHeader, CitationBlock } from '../components/primitives.jsx';

export default function Dashboard() {
  const nav = useNavigate();
  const totalCost = calcCostRange();
  const noncompliant = STATEWIDE_GRADES.slice(1, 4).reduce((s, g) => s + g.count, 0);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-[#0a0a0a] to-[#2a2a2a] text-white p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#DBC078]/10 rounded-full blur-3xl -translate-y-12 translate-x-12"></div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <Pill variant="gold">Live data</Pill>
            <Pill className="bg-white/10 text-white border-white/20">VDOT inventory · May 2026</Pill>
          </div>
          <h1 className="text-4xl mb-3 leading-tight font-serif-display">Virginia ADA Curb Ramp Compliance Overview</h1>
          <p className="text-stone-300 max-w-3xl mb-5 leading-relaxed">
            Statewide visibility into Virginia's 82,438 VDOT-maintained ADA curb ramps. PROWAG R3 federal floor measured alongside VDOT IIM-TE-376.1 functional condition grades for every record.
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => nav('/inventory')} className="bg-[#DBC078] hover:bg-[#c4a85a] text-[#0a0a0a] font-medium px-4 py-2 text-sm uppercase tracking-wider">
              Explore inventory →
            </button>
            <button onClick={() => nav('/report')} className="bg-white/10 hover:bg-white/15 text-white border border-white/20 px-4 py-2 text-sm uppercase tracking-wider">
              Sample tenant report
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total VDOT ramps" value={fmt(STATEWIDE_TOTAL)} sublabel="All 9 districts · VDOT ADA Hub" icon={Database} accent="navy" />
        <StatCard label="Noncompliant (B+C+D)" value={fmt(noncompliant)} sublabel="~62% per IIM-TE-376.1" icon={AlertTriangle} accent="burgundy" />
        <StatCard label="Statewide cost projection" value={`${fmtCurrency(totalCost.low)}–${fmtCurrency(totalCost.high)}`} sublabel="Norfolk benchmark $2,200/ramp" icon={DollarSign} accent="gold" />
        <StatCard label="Fairfax (top jurisdiction)" value={fmt(43611)} sublabel="52.9% of statewide inventory" icon={MapPin} accent="teal" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-stone-200 p-6">
          <SectionHeader sub="VDOT 9-district breakdown">Statewide distribution</SectionHeader>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={DISTRICTS} layout="vertical" margin={{ left: 90, right: 30, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" horizontal={false} />
              <XAxis type="number" stroke="#78716c" style={{ fontSize: '11px' }} />
              <YAxis dataKey="name" type="category" stroke="#78716c" style={{ fontSize: '11px' }} width={85} />
              <Tooltip contentStyle={{ background: '#fafaf9', border: '1px solid #d6d3d1', fontSize: '12px' }} formatter={(v) => [fmt(v) + ' ramps', 'Count']} />
              <Bar dataKey="count" fill="#0a0a0a" />
            </BarChart>
          </ResponsiveContainer>
          <CitationBlock source="VDOT ADA Curb Ramp Condition Map (Hub item c4cfead89d1d4051837b7e6ab523f6aa); CRUB_ADA_VA_MAY_2026.json (82,438 features)." />
        </div>

        <div className="lg:col-span-2 bg-white border border-stone-200 p-6">
          <SectionHeader sub="VDOT IIM-TE-376.1 derived">Grade distribution</SectionHeader>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={STATEWIDE_GRADES} dataKey="count" nameKey="grade" cx="50%" cy="50%" outerRadius={85} innerRadius={40} stroke="#fafaf9" strokeWidth={2} label={({ grade }) => grade} labelLine={false}>
                {STATEWIDE_GRADES.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#fafaf9', border: '1px solid #d6d3d1', fontSize: '12px' }} formatter={(v, n, p) => [`${fmt(v)} ramps (${fmtPct(v, STATEWIDE_TOTAL)})`, p.payload.label]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 text-xs mt-3">
            {STATEWIDE_GRADES.map((g) => (
              <div key={g.grade} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3" style={{ background: g.color }}></div>
                  <span className="font-medium text-stone-700">{g.grade}</span>
                  <span className="text-stone-500">{g.label}</span>
                </div>
                <span className="font-mono text-stone-700">{fmt(g.count)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="PROWAG R305.1.1 specifies truncated dome; exposed aggregate is pre-2010 legacy and NOT compliant">
          Detectable warning surface compliance · the wedge
        </SectionHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={DWS_DATA} margin={{ top: 5, right: 5, left: 5, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
              <XAxis dataKey="name" stroke="#78716c" angle={-25} textAnchor="end" style={{ fontSize: '10px' }} height={50} interval={0} />
              <YAxis stroke="#78716c" style={{ fontSize: '11px' }} />
              <Tooltip contentStyle={{ background: '#fafaf9', border: '1px solid #d6d3d1', fontSize: '12px' }} formatter={(v) => fmt(v) + ' ramps'} />
              <Bar dataKey="count">
                {DWS_DATA.map((d, i) => <Cell key={i} fill={d.prowag === true ? '#1F7A8C' : d.prowag === false ? '#7A1F2B' : '#999'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="space-y-2">
            <div className="bg-[#7A1F2B]/5 border-l-4 border-[#7A1F2B] p-4">
              <div className="text-xs font-medium uppercase tracking-wider text-[#7A1F2B] mb-2">The headline finding</div>
              <div className="text-stone-700 leading-relaxed">
                <span className="text-2xl text-[#7A1F2B] font-serif-display">26,754</span>
                <span className="ml-2">VDOT ramps have <strong>Exposed Aggregate</strong> DWS, a pre-2010 pattern that does NOT comply with PROWAG R305.1.1.</span>
              </div>
              <div className="text-xs text-stone-500 mt-3">Defensible noncompliance finding for 26,754 ramps before AtlasPath measures anything. Compare to <strong>18,633</strong> compliant Truncated Dome.</div>
            </div>
            <CitationBlock source="PROWAG R305.1.1 (88 FR 53604); VDOT IIM-TE-376.1 (rev. April 2024); VTRC 21-R18 (March 2021)." />
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="Norfolk benchmark $2,200/ramp · DWS $500–1,500 · overlap not deduped (upper bound)">
          Statewide remediation cost projection
        </SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            {COST_CATEGORIES.map((cat) => (
              <div key={cat.category} className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0">
                <div className="flex-1">
                  <div className="text-sm text-stone-700">{cat.category}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{fmt(cat.count)} ramps × ${fmt(cat.unit_low)}{cat.unit_low !== cat.unit_high ? `–$${fmt(cat.unit_high)}` : ''}</div>
                </div>
                <div className="font-mono text-sm text-[#0a0a0a] font-medium">
                  {fmtCurrency(cat.count * cat.unit_low)}{cat.unit_low !== cat.unit_high ? `–${fmtCurrency(cat.count * cat.unit_high)}` : ''}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between py-3 pt-4 border-t-2 border-[#0a0a0a]/20">
              <div className="text-lg text-[#0a0a0a] font-serif-display">Aggregate</div>
              <div className="text-2xl text-[#0a0a0a] font-serif-display">{fmtCurrency(totalCost.low)}–{fmtCurrency(totalCost.high)}</div>
            </div>
          </div>
          <div className="bg-stone-50 border border-stone-200 p-4 text-xs text-stone-600 leading-relaxed space-y-2">
            <div className="font-medium uppercase tracking-wider text-[#0a0a0a] text-[10px]">Methodology note</div>
            <p>Upper bound. Many ramps fail multiple categories simultaneously (36"-width ramp with exposed aggregate DWS counts in both). Dedup is a follow-on engagement candidate.</p>
            <p>Unit costs sourced from path.regulations (operating directive 8: no hardcoded thresholds). Norfolk benchmark from VA ADA Transition Plan (2019, rev. 2023).</p>
            <p>Federal funding eligible: SS4A, HSIP, CDBG, RAISE; VDOT match per IIM-LD-55.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="Click Fairfax to view AtlasPath tenant report">
          Top jurisdictions · NoVA dominant (84.4% of state)
        </SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {JURISDICTIONS.map((j) => {
            const pct = (j.count / STATEWIDE_TOTAL * 100).toFixed(1);
            return (
              <button
                key={j.code}
                onClick={() => nav(`/report?slug=${j.slug}`)}
                className="text-left bg-stone-50 hover:bg-[#0a0a0a]/5 border border-stone-200 hover:border-[#0a0a0a]/30 p-5 transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-lg text-[#0a0a0a] font-serif-display">{j.name}</div>
                    <div className="text-xs text-stone-500">VDOT code {j.code} · {j.district}</div>
                  </div>
                  <ArrowUpRight size={16} className="text-[#1F7A8C] group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm"><span className="text-stone-600">Ramps</span><span className="font-mono font-medium text-stone-800">{fmt(j.count)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-stone-600">Share</span><span className="font-mono text-stone-800">{pct}%</span></div>
                  <div className="flex justify-between text-sm"><span className="text-stone-600">Remediation</span><span className="font-mono text-[#7A1F2B] font-medium">${j.cost_low_m}–${j.cost_high_m}M</span></div>
                  <div className="flex justify-between text-sm"><span className="text-stone-600">APCI</span><span className="font-mono text-[#0a0a0a] font-medium">{j.apci} · {j.apci_category}</span></div>
                </div>
                <div className="mt-3 pt-3 border-t border-stone-200 text-xs text-[#1F7A8C]">View sample tenant report →</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
